// === Admin: SWMS Tasks Backends ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from old Click2Gro (app/Controllers/Admin/SwmsTasksBackendsController.php)
// Fields: tasks, hazards_risks, control_measures, risk_ranking (FK), consequence,
//         colour_background, colour_text

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SwmsTasksBackendRow {
  readonly id: number;
  readonly tasks: string | null;
  readonly hazards_risks: string | null;
  readonly control_measures: string | null;
  readonly risk_ranking: number | null;
  readonly consequence: string | null;
  readonly colour_background: string | null;
  readonly colour_text: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
}

interface RiskRankingOption {
  readonly id: number;
  readonly risk_ranking_harm: string | null;
  readonly consequence: string | null;
  readonly colour_background: string | null;
  readonly colour_text: string | null;
}

interface FormState {
  tasks: string;
  hazards_risks: string;
  control_measures: string;
  risk_ranking: number | '';
  consequence: string;
  colour_background: string;
  colour_text: string;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

const DEFAULT_FORM: FormState = {
  tasks: '',
  hazards_risks: '',
  control_measures: '',
  risk_ranking: '',
  consequence: '',
  colour_background: '#ffffff',
  colour_text: '#000000',
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

function truncate(text: string | null, max = 60): string {
  if (!text) return '—';
  return text.length <= max ? text : text.slice(0, max) + '…';
}

// ---------------------------------------------------------------------------
// SwmsTasksBackendsPage
// ---------------------------------------------------------------------------

export function SwmsTasksBackendsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  // List state
  const [rows, setRows]               = useState<SwmsTasksBackendRow[]>([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [loading, setLoading]       = useState(false);
  const [listError, setListError]   = useState<string | null>(null);

  // Risk ranking options for dropdown
  const [riskRankings, setRiskRankings] = useState<RiskRankingOption[]>([]);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);
  const [form, setForm]         = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving]     = useState(false);

  // Delete dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const token = session?.access_token ?? '';

  // -------------------------------------------------------------------------
  // Fetch risk ranking dropdown options
  // -------------------------------------------------------------------------
  const fetchRiskRankings = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${getApiUrl()}/api/v1/admin/swms-tasks-backends/risk-rankings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json() as RiskRankingOption[];
    setRiskRankings(Array.isArray(json) ? json : []);
  }, [token]);

  useEffect(() => { void fetchRiskRankings(); }, [fetchRiskRankings]);

  // -------------------------------------------------------------------------
  // Fetch list
  // -------------------------------------------------------------------------
  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page:  String(page + 1),
        limit: String(rowsPerPage),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await fetch(`${getApiUrl()}/api/v1/admin/swms-tasks-backends?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json() as { data: SwmsTasksBackendRow[]; total: number };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch {
      setListError('Failed to load SWMS tasks');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchRows(); }, [fetchRows]);

  // -------------------------------------------------------------------------
  // Form open/close
  // -------------------------------------------------------------------------
  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setFormOpen(true);
  };

  const openEdit = (row: SwmsTasksBackendRow) => {
    setEditId(row.id);
    const rankingOption = riskRankings.find((r) => r.id === row.risk_ranking);
    setForm({
      tasks:             row.tasks             ?? '',
      hazards_risks:     row.hazards_risks     ?? '',
      control_measures:  row.control_measures  ?? '',
      risk_ranking:      row.risk_ranking      ?? '',
      consequence:       row.consequence       ?? '',
      colour_background: row.colour_background ?? rankingOption?.colour_background ?? '#ffffff',
      colour_text:       row.colour_text       ?? rankingOption?.colour_text       ?? '#000000',
      created_at:        row.created_at        ?? null,
      created_by:        row.created_by        ?? null,
      updated_at:        row.updated_at        ?? null,
      updated_by:        row.updated_by        ?? null,
    });
    setFormOpen(true);
  };

  const closeForm = () => { setFormOpen(false); };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // When risk ranking is selected, auto-populate consequence + colours from the option data
  const handleRiskRankingChange = (rankingId: number | '') => {
    setField('risk_ranking', rankingId);
    if (rankingId === '') return;
    const option = riskRankings.find((r) => r.id === rankingId);
    if (!option) return;
    setForm((prev) => ({
      ...prev,
      risk_ranking:      rankingId,
      consequence:       option.consequence       ?? prev.consequence,
      colour_background: option.colour_background ?? prev.colour_background,
      colour_text:       option.colour_text       ?? prev.colour_text,
    }));
  };

  // -------------------------------------------------------------------------
  // Save (add / edit)
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.tasks.trim()) return;
    setSaving(true);
    try {
      const url = editId
        ? `${getApiUrl()}/api/v1/admin/swms-tasks-backends/${editId}`
        : `${getApiUrl()}/api/v1/admin/swms-tasks-backends`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          tasks:             form.tasks.trim(),
          hazards_risks:     form.hazards_risks.trim()    || null,
          control_measures:  form.control_measures.trim() || null,
          risk_ranking:      form.risk_ranking !== ''     ? form.risk_ranking : null,
          consequence:       form.consequence.trim()      || null,
          colour_background: form.colour_background       || null,
          colour_text:       form.colour_text             || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(err.error ?? 'Save failed');
      }
      setSnack({ open: true, msg: editId ? 'Task updated' : 'Task added', severity: 'success' });
      closeForm();
      void fetchRows();
    } catch (e) {
      setSnack({ open: true, msg: e instanceof Error ? e.message : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await fetch(`${getApiUrl()}/api/v1/admin/swms-tasks-backends/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnack({ open: true, msg: 'Task deleted', severity: 'success' });
      setDeleteId(null);
      void fetchRows();
    } catch {
      setSnack({ open: true, msg: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  // Selected risk ranking option (for live preview in form)
  const selectedRanking = riskRankings.find((r) => r.id === form.risk_ranking) ?? null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>SWMS Tasks Backends</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage reusable SWMS task entries with hazards, risk rankings, and control measures
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Task
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search tasks, hazards or measures…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 360 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Task</AppTableHeadCell>
                  <AppTableHeadCell>Hazards / Risks</AppTableHeadCell>
                  <AppTableHeadCell>Control Measures</AppTableHeadCell>
                  <AppTableHeadCell>Risk Ranking</AppTableHeadCell>
                  <AppTableHeadCell>Consequence</AppTableHeadCell>
                  <AppTableHeadCell>Updated</AppTableHeadCell>
                  <AppTableHeadCell align="right">Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No tasks found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const ranking = riskRankings.find((r) => r.id === row.risk_ranking) ?? null;
                    return (
                      <AppTableRow key={row.id}>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {truncate(row.tasks, 80)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" color="text.secondary">
                            {truncate(row.hazards_risks, 70)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" color="text.secondary">
                            {truncate(row.control_measures, 70)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ranking ? (
                            <Chip
                              label={truncate(ranking.risk_ranking_harm, 30)}
                              size="small"
                              sx={{
                                bgcolor:    row.colour_background ?? ranking.colour_background ?? undefined,
                                color:      '#000000',
                                fontWeight: 600,
                                fontSize:   12,
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {truncate(row.consequence, 50)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {fmtDate(row.updated_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </AppTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>{editId ? 'Edit SWMS Task' : 'Add SWMS Task'}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label="Task"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.tasks}
              onChange={(e) => setField('tasks', e.target.value)}
            />
            <TextField
              label="Hazards / Risks"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.hazards_risks}
              onChange={(e) => setField('hazards_risks', e.target.value)}
            />
            <TextField
              label="Control Measures"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.control_measures}
              onChange={(e) => setField('control_measures', e.target.value)}
            />

            {/* Risk ranking dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel id="risk-ranking-label">Risk Ranking</InputLabel>
              <Select
                labelId="risk-ranking-label"
                label="Risk Ranking"
                value={form.risk_ranking}
                onChange={(e) => handleRiskRankingChange(e.target.value as number | '')}
                renderValue={(val) => {
                  if (typeof val !== 'number') return <em>— None —</em>;
                  const opt = riskRankings.find((r) => r.id === val);
                  if (!opt) return String(val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 16, height: 16, borderRadius: 0.5, flexShrink: 0,
                        bgcolor: opt.colour_background ?? 'grey.300',
                        border: '1px solid', borderColor: 'divider',
                      }} />
                      {opt.risk_ranking_harm}
                    </Box>
                  );
                }}
              >
                <MenuItem value=""><em>— None —</em></MenuItem>
                {riskRankings.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 16, height: 16, borderRadius: 0.5, flexShrink: 0,
                        bgcolor: r.colour_background ?? 'grey.300',
                        border: '1px solid', borderColor: 'divider',
                      }} />
                      {r.risk_ranking_harm}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Auto-populated consequence (read-only preview, editable override) */}
            <TextField
              label="Consequence"
              fullWidth
              size="small"
              value={form.consequence}
              onChange={(e) => setField('consequence', e.target.value)}
              inputProps={{ maxLength: 120 }}
              helperText="Auto-filled from risk ranking — you can override if needed"
            />

            {/* Colour swatches — auto-populated from ranking, overrideable */}
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Background Colour
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    value={form.colour_background}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setField('colour_background', e.target.value)
                    }
                    sx={{
                      width: 44,
                      height: 36,
                      padding: 0,
                      border: 'none',
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      overflow: 'hidden',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'transparent',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {form.colour_background}
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Text Colour
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    value={form.colour_text}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setField('colour_text', e.target.value)
                    }
                    sx={{
                      width: 44,
                      height: 36,
                      padding: 0,
                      border: 'none',
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      overflow: 'hidden',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'transparent',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {form.colour_text}
                  </Typography>
                </Stack>
              </Box>
              {/* Live preview */}
              <Box sx={{ ml: 'auto !important' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Preview
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: form.colour_background,
                    color: '#000000',
                    border: '1px solid',
                    borderColor: 'divider',
                    minWidth: 120,
                    textAlign: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {selectedRanking?.risk_ranking_harm ?? 'Preview'}
                </Box>
              </Box>
            </Stack>
          </Stack>

          {/* Audit Trail */}
          {editId && (
            <Box
              mt={2}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(form.created_at)}{form.created_by ? <> &mdash; <strong>{form.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(form.updated_at)}{form.updated_by ? <> &mdash; <strong>{form.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !form.tasks.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this task? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
